import type { Redactor, RedactorOptions } from "../interfaces/scrubber";

export const REDACTED = "[redacted]";
export const TOKEN_PATTERNS: readonly RegExp[] = [
  /eyJ[A-Za-z0-9_-]{16,}(?:\.[A-Za-z0-9_-]*){0,2}/g,
  /ya29\.[A-Za-z0-9_-]{16,}/g,
  /\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g,
  /IQoJb3JpZ2lu[A-Za-z0-9+/=_-]{16,}/g,
  /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{8,}/g,
  /\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}/g,
  /\bgsk_[A-Za-z0-9]{16,}/g,
  /\bwhsec_[A-Za-z0-9]{8,}/g,
  /\bgh[pousr]_[A-Za-z0-9]{20,}/g,
  /\bgithub_pat_[A-Za-z0-9_]{20,}/g,
  /\bxox[abprs]-[A-Za-z0-9-]{10,}/g,
  /\bAIza[A-Za-z0-9_-]{30,}/g,
  /\bSG\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}/g,
  /\bnpm_[A-Za-z0-9]{16,}/g,
];

const QUERY_STRING_KEY = "query_string";
const SECRET_PARAM_NAMES = new Set(["policy", "keypairid", "awsaccesskeyid", "sig", "codeverifier"]);
const SHORT_PARAM_NAMES = new Set(["code", "t", "tk", "otp", "pin"]);
const URL_PARAM_BOUNDARY = /^(?:|[?&#]|%3F|%26)$/i;
const QUERY_CONTINUES = /^(?:&|%26)/i;
const REDACTION_MARK = /^\[[a-z ]{1,20}\]$/;
const SECRET_PARAM_NAME_ENDING = /(?:token|jwt|session|auth|authorization|key|secret|password|passwd|pwd|signature|credential|cookie)$/;
const QUERY_PARAM = /(^|[?&#;\s=]|%3F|%26|%3D)([A-Za-z][\w.-]{0,63})(=|%3D)("[^"]*"?|'[^']*'?|(?:(?!%26|%3D)[^&#\s"'=])*)/gi;
const PARAM_VALUE_REST = /(?:(?!%26)[^&#\s"'])*/y;
const QUOTED_VALUE = /^["']/;

export function passesLuhn(digits: string): boolean {
  let sum = 0;
  for (let index = 0; index < digits.length; index++) {
    let digit = Number(digits[digits.length - 1 - index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

export function stripQuery(url: string): string {
  return url.split(/[?#]/)[0] ?? url;
}

function isSecretParamName(name: string, inUrl = true): boolean {
  const normalized = name.toLowerCase().replace(/[-_.]/g, "");
  if (SHORT_PARAM_NAMES.has(normalized)) return inUrl;
  return SECRET_PARAM_NAMES.has(normalized) || SECRET_PARAM_NAME_ENDING.test(normalized);
}

function unquotedValueEnd(text: string, valueStart: number): number {
  PARAM_VALUE_REST.lastIndex = valueStart;
  return valueStart + (PARAM_VALUE_REST.exec(text)?.[0].length ?? 0);
}

function redactSecretParams(text: string): string {
  let result = "";
  let copied = 0;
  let unquotedEnd = -1;
  let urlValueEnd = -1;
  QUERY_PARAM.lastIndex = 0;
  for (let found = QUERY_PARAM.exec(text); found; found = QUERY_PARAM.exec(text)) {
    const [match, boundary, name, equals, value] = found;
    const valueStart = found.index + match.length - value.length;
    QUERY_PARAM.lastIndex = valueStart - equals.length;
    let valueEnd = valueStart + value.length;
    if (!QUOTED_VALUE.test(value)) {
      if (valueStart > unquotedEnd) unquotedEnd = unquotedValueEnd(text, valueStart);
      valueEnd = unquotedEnd;
    }
    const inUrl = found.index < urlValueEnd || URL_PARAM_BOUNDARY.test(boundary) || QUERY_CONTINUES.test(text.slice(valueEnd, valueEnd + 3));
    const fullValue = text.slice(valueStart, valueEnd);
    if (fullValue && !REDACTION_MARK.test(fullValue) && isSecretParamName(name, inUrl)) {
      result += text.slice(copied, valueStart) + REDACTED;
      copied = valueEnd;
      QUERY_PARAM.lastIndex = valueEnd;
    } else if (inUrl) {
      urlValueEnd = Math.max(urlValueEnd, valueEnd);
    }
  }
  return result + text.slice(copied);
}

function redactQueryPairs(query: unknown): unknown {
  if (Array.isArray(query)) {
    return query.map((pair) => (Array.isArray(pair) && typeof pair[0] === "string" && isSecretParamName(pair[0]) ? [pair[0], REDACTED] : pair));
  }
  if (query && typeof query === "object") {
    return Object.fromEntries(Object.entries(query).map(([name, value]) => [name, isSecretParamName(name) ? REDACTED : value]));
  }
  return query;
}

export function createRedactor(options: RedactorOptions): Redactor {
  const { textRules, maxTextLength, truncatedMark, isSecretEntry, maxDepth, redactKeyNames, redactNumber } = options;

  function redactSecrets(text: string): string {
    const capped = text.length > maxTextLength ? `${text.slice(0, maxTextLength)}${truncatedMark}` : text;
    return redactSecretParams(textRules.reduce((result, rule) => result.replace(rule.pattern, rule.replacement as string), capped));
  }

  function redactValue<T>(value: T, depth = 0): T {
    if (typeof value === "string") return redactSecrets(value) as T;
    if (typeof value === "number" && redactNumber) return redactNumber(value) as T;
    if (!value || typeof value !== "object") return value;
    if (maxDepth !== undefined && depth >= maxDepth) return value;
    if (Array.isArray(value)) return value.map((item) => redactValue(item, depth + 1)) as T;
    return Object.fromEntries(
      Object.entries(value).map(([key, inner]) => [
        redactKeyNames ? redactSecrets(key) : key,
        isSecretEntry(key, inner) ? REDACTED : redactValue(key === QUERY_STRING_KEY ? redactQueryPairs(inner) : inner, depth + 1),
      ]),
    ) as T;
  }

  return { redactSecrets, redactValue };
}

export function failClosed<A extends unknown[], R, F>(scrub: (...args: A) => R, fallback: (...args: A) => F): (...args: A) => R | F {
  return (...args) => {
    try {
      return scrub(...args);
    } catch {
      return fallback(...args);
    }
  };
}
